// src/components/ProtectedRoute/Admin/Consoles/Pages/ScientificNamesConsole.jsx
import React, { useState } from "react";
import { Box, Button, IconButton, Stack, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Checkbox, FormControlLabel } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import AddIcon from "@mui/icons-material/Add";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/WarningAmber";
import { DataGrid } from "@mui/x-data-grid";
import ConsolePageFrame from "../Framework/ConsolePageFrame";
import scientificNamesPlugin from "./scientificNames.plugin";
import { fetchScientificNamesByParent } from "./scientificNameService";
import DialogScientificNameEditor from "../../Dialogs/DialogScientificNameEditor";
import DialogGbifBulkImport from "../../Dialogs/DialogGbifBulkImport";
import DialogGbifBulkImportV2 from "../../Dialogs/DialogGbifBulkImportV2";
import DialogGbifHierarchicalImport from "../../Dialogs/DialogGbifHierarchicalImport/DialogGbifHierarchicalImport";
import DialogScientificNameExcelImport from "../../Dialogs/DialogScientificNameExcelImport";
import DialogScientificNameDocs from "../../Dialogs/DialogScientificNameDocs";
import DialogScientificNameBulkMoveChildren from "./DialogScientificNameBulkMoveChildren";
import supabase from "../../../../../utils/supabase";
import { useSupabaseSession } from "../../../../../utils/AuthSessionProvider";
import { useToast } from "../Framework/ToastProvider";
import { useStatusDialog } from "../../../../common/StatusDialogProvider";

const TAXONOMIC_ACT_REF_COLUMNS = [
  "related_name_id",
  "scientific_name_id",
  "replacement_name_id",
  "type_taxon_id",
];

export default function ScientificNamesConsole() {
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [gbifOpen, setGbifOpen] = useState(false);
  const [gbifV2Open, setGbifV2Open] = useState(false);
  const [gbifV3Open, setGbifV3Open] = useState(false);
  const [docsRow, setDocsRow] = useState(null);
  const [childFilterStack, setChildFilterStack] = useState([]); // [{ id, label }]
  const [addChildParent, setAddChildParent] = useState(null);
  const [bulkMoveTarget, setBulkMoveTarget] = useState(null);
  const [externalRefreshKey, setExternalRefreshKey] = useState(0);
  const [refDialog, setRefDialog] = useState({ open: false, rows: [], targetIds: [] });
  const [editRowInRef, setEditRowInRef] = useState(null);
  const [deleteTreeDialog, setDeleteTreeDialog] = useState({
    open: false,
    loading: false,
    target: null,
    ids: [],
    rows: [],
    originalParentRefs: [],
    typeTaxaRefs: [],
    taxonomicActRefs: [],
    vernacularNameRefs: [],
    error: null,
  });
  const [deleteTreeDeleting, setDeleteTreeDeleting] = useState(false);
  const [deleteTreeAckOriginalParent, setDeleteTreeAckOriginalParent] = useState(false);
  const [deleteTreeAckTypeTaxa, setDeleteTreeAckTypeTaxa] = useState(false);
  const [deleteTreeAckTaxonomicActs, setDeleteTreeAckTaxonomicActs] = useState(false);
  const [deleteTreeAckVernacularNames, setDeleteTreeAckVernacularNames] = useState(false);

  const { user } = useSupabaseSession();
  const isAdmin = user?.app_metadata?.role === "admin";
  const toast = useToast();
  const { showStatus } = useStatusDialog();

  const currentParent = childFilterStack.length ? childFilterStack[childFilterStack.length - 1] : null;

  const effectivePlugin = React.useMemo(() => {
    if (!currentParent) return scientificNamesPlugin;
    return {
      ...scientificNamesPlugin,
      defaultSort: { field: "name_spell_valid", sort: "asc" },
      // Override fetch to return parent + direct children only
      fetch: async ({ orderBy, ascending }) => {
        return fetchScientificNamesByParent({
          parentId: currentParent.id,
          orderBy,
          ascending,
          normalize: scientificNamesPlugin.normalize,
        });
      },
    };
  }, [currentParent]);

  const handleShowChildrenInline = React.useCallback((row) => {
    if (!row?.id) return;
    setChildFilterStack((prev) => {
      const label = row._nameValidDisp || row.name_spell_valid || row.id;
      const existingIdx = prev.findIndex((f) => f.id === row.id);
      if (existingIdx >= 0) {
        // Jump back to this level
        return prev.slice(0, existingIdx + 1);
      }
      return [...prev, { id: row.id, label }];
    });
  }, []);

  const handleAddChild = React.useCallback(
    (row) => {
      if (!row?.id) return;
      handleShowChildrenInline(row);
      setAddChildParent(row);
    },
    [handleShowChildrenInline]
  );

  const handleFilterButtonClick = (idx) => {
    setChildFilterStack((prev) => {
      if (idx <= 0) return [];
      return prev.slice(0, idx);
    });
  };

  const handleClearAllFilters = () => setChildFilterStack([]);

  const normalizeId = (value) => (value === null || value === undefined ? "" : String(value));

  const chunkIds = (values = [], size = 100) => {
    const chunks = [];
    for (let i = 0; i < values.length; i += size) {
      chunks.push(values.slice(i, i + size));
    }
    return chunks;
  };

  const collectSubtree = React.useCallback(async (rootRow) => {
    const rootId = normalizeId(rootRow?.id);
    if (!rootId) {
      return { ids: [], rows: [], originalParentRefs: [], typeTaxaRefs: [], taxonomicActRefs: [], vernacularNameRefs: [] };
    }

    const seen = new Set([rootId]);
    const ids = [rootId];
    const rowsMap = new Map();
    rowsMap.set(rootId, {
      id: rootId,
      name_spell_valid: rootRow?._nameValidDisp || rootRow?.name_spell_valid || "",
      current_rank: rootRow?.current_rank || "",
      current_parent: rootRow?.current_parent || null,
    });

    const queue = [rootId];
    while (queue.length) {
      const batch = queue.splice(0, 200);
      const { data, error } = await supabase
        .from("scientific_names")
        .select("id,name_spell_valid,current_rank,current_parent")
        .in("current_parent", batch);
      if (error) throw error;

      (data || []).forEach((row) => {
        const id = normalizeId(row.id);
        if (!id || seen.has(id)) return;
        seen.add(id);
        ids.push(id);
        rowsMap.set(id, { ...row, id });
        queue.push(id);
      });
    }

    const originalParentMap = new Map();
    for (const batch of chunkIds(ids, 100)) {
      const { data, error } = await supabase
        .from("scientific_names")
        .select("id,name_spell_valid,current_rank,original_parent")
        .in("original_parent", batch);
      if (error) throw error;
      (data || []).forEach((row) => {
        const id = normalizeId(row.id);
        if (!id) return;
        if (seen.has(id)) return;
        if (!originalParentMap.has(id)) {
          originalParentMap.set(id, { ...row, id });
        }
      });
    }

    const typeTaxaMap = new Map();
    for (const batch of chunkIds(ids, 100)) {
      const { data, error } = await supabase
        .from("scientific_names")
        .select("id,name_spell_valid,current_rank,type_taxa_id")
        .in("type_taxa_id", batch);
      if (error) throw error;
      (data || []).forEach((row) => {
        const id = normalizeId(row.id);
        if (!id) return;
        if (seen.has(id)) return;
        if (!typeTaxaMap.has(id)) {
          typeTaxaMap.set(id, { ...row, id });
        }
      });
    }

    const taxonomicActMap = new Map();
    for (const column of TAXONOMIC_ACT_REF_COLUMNS) {
      for (const batch of chunkIds(ids, 100)) {
        const { data, error } = await supabase
          .from("taxonomic_acts")
          .select("id,related_name_id,scientific_name_id,replacement_name_id,type_taxon_id,act_type_id,publication_id")
          .in(column, batch);
        if (error) throw error;
        (data || []).forEach((row) => {
          const id = normalizeId(row.id);
          if (!id) return;
          const existing = taxonomicActMap.get(id);
          const refFields = existing?._refFields ? [...existing._refFields] : [];
          if (!refFields.includes(column)) refFields.push(column);
          taxonomicActMap.set(id, { ...(existing || row), ...row, id, _refFields: refFields });
        });
      }
    }

    const vernacularMap = new Map();
    for (const batch of chunkIds(ids, 100)) {
      const { data, error } = await supabase
        .from("vernacular_names")
        .select("id,scientific_name_id")
        .in("scientific_name_id", batch);
      if (error) throw error;
      (data || []).forEach((row) => {
        const id = normalizeId(row.id);
        if (!id) return;
        if (!vernacularMap.has(id)) vernacularMap.set(id, { ...row, id });
      });
    }

    return {
      ids,
      rows: Array.from(rowsMap.values()),
      originalParentRefs: Array.from(originalParentMap.values()),
      typeTaxaRefs: Array.from(typeTaxaMap.values()),
      taxonomicActRefs: Array.from(taxonomicActMap.values()),
      vernacularNameRefs: Array.from(vernacularMap.values()),
    };
  }, []);

  const handleOpenDeleteTree = React.useCallback(
    async (row) => {
      if (!row?.id) return;
      if (!isAdmin) {
        showStatus({
          severity: "error",
          title: "Permission denied",
          description: "Admin role is required to delete a subtree.",
        });
        return;
      }
      setDeleteTreeAckOriginalParent(false);
      setDeleteTreeAckTypeTaxa(false);
      setDeleteTreeAckTaxonomicActs(false);
      setDeleteTreeAckVernacularNames(false);
      setDeleteTreeDialog({
        open: true,
        loading: true,
        target: row,
        ids: [],
        rows: [],
        originalParentRefs: [],
        typeTaxaRefs: [],
        taxonomicActRefs: [],
        vernacularNameRefs: [],
        error: null,
      });
      try {
        const result = await collectSubtree(row);
        setDeleteTreeDialog((prev) => ({
          ...prev,
          loading: false,
          ids: result.ids,
          rows: result.rows,
          originalParentRefs: result.originalParentRefs || [],
          typeTaxaRefs: result.typeTaxaRefs || [],
          taxonomicActRefs: result.taxonomicActRefs || [],
          vernacularNameRefs: result.vernacularNameRefs || [],
        }));
      } catch (e) {
        setDeleteTreeDialog((prev) => ({
          ...prev,
          loading: false,
          error: e?.message || "Failed to load subtree.",
        }));
      }
    },
    [collectSubtree, isAdmin, showStatus]
  );

  const handleCloseDeleteTree = React.useCallback(() => {
    if (deleteTreeDeleting) return;
    setDeleteTreeAckOriginalParent(false);
    setDeleteTreeAckTypeTaxa(false);
    setDeleteTreeAckTaxonomicActs(false);
    setDeleteTreeAckVernacularNames(false);
    setDeleteTreeDialog({
      open: false,
      loading: false,
      target: null,
      ids: [],
      rows: [],
      originalParentRefs: [],
      typeTaxaRefs: [],
      taxonomicActRefs: [],
      vernacularNameRefs: [],
      error: null,
    });
  }, [deleteTreeDeleting]);

  const handleConfirmDeleteTree = React.useCallback(async () => {
    if (!deleteTreeDialog.ids.length || deleteTreeDeleting) return;
    if (!isAdmin) {
      showStatus({
        severity: "error",
        title: "Permission denied",
        description: "Admin role is required to delete a subtree.",
      });
      return;
    }
    if (deleteTreeDialog.originalParentRefs.length && !deleteTreeAckOriginalParent) {
      showStatus({
        severity: "warning",
        title: "Confirmation required",
        description: "Please confirm clearing original_parent links before deleting.",
      });
      return;
    }
    if (deleteTreeDialog.typeTaxaRefs.length && !deleteTreeAckTypeTaxa) {
      showStatus({
        severity: "warning",
        title: "Confirmation required",
        description: "Please confirm clearing type_taxa_id links before deleting.",
      });
      return;
    }
    if (deleteTreeDialog.taxonomicActRefs.length && !deleteTreeAckTaxonomicActs) {
      showStatus({
        severity: "warning",
        title: "Confirmation required",
        description: "Please confirm deleting related taxonomic acts before deleting the subtree.",
      });
      return;
    }
    if (deleteTreeDialog.vernacularNameRefs.length && !deleteTreeAckVernacularNames) {
      showStatus({
        severity: "warning",
        title: "Confirmation required",
        description: "Please confirm deleting related vernacular names before deleting the subtree.",
      });
      return;
    }
    setDeleteTreeDeleting(true);
    try {
      for (const batch of chunkIds(deleteTreeDialog.ids, 100)) {
        const { error: updErr } = await supabase
          .from("scientific_names")
          .update({ original_parent: null })
          .in("original_parent", batch);
        if (updErr) throw updErr;
      }
      for (const batch of chunkIds(deleteTreeDialog.ids, 100)) {
        const { error: updErr } = await supabase
          .from("scientific_names")
          .update({ type_taxa_id: null })
          .in("type_taxa_id", batch);
        if (updErr) throw updErr;
      }
      if (deleteTreeDialog.vernacularNameRefs.length) {
        for (const batch of chunkIds(deleteTreeDialog.ids, 100)) {
          const { error: delErr } = await supabase
            .from("vernacular_names")
            .delete()
            .in("scientific_name_id", batch);
          if (delErr) throw delErr;
        }
      }
      if (deleteTreeDialog.taxonomicActRefs.length) {
        for (const column of TAXONOMIC_ACT_REF_COLUMNS) {
          for (const batch of chunkIds(deleteTreeDialog.ids, 100)) {
            const { error: delErr } = await supabase
              .from("taxonomic_acts")
              .delete()
              .in(column, batch);
            if (delErr) throw delErr;
          }
        }
      }
      const deleteIds = [...deleteTreeDialog.ids].reverse();
      for (const batch of chunkIds(deleteIds, 100)) {
        const { error } = await supabase.from("scientific_names").delete().in("id", batch);
        if (error) throw error;
      }
      const clearedRefs = deleteTreeDialog.originalParentRefs.length;
      const clearedTypeTaxa = deleteTreeDialog.typeTaxaRefs.length;
      const deletedVernacular = deleteTreeDialog.vernacularNameRefs.length;
      const deletedActs = deleteTreeDialog.taxonomicActRefs.length;
      toast?.push(
        "success",
        clearedRefs || clearedTypeTaxa || deletedActs || deletedVernacular
          ? `Cleared ${clearedRefs} original_parent links, cleared ${clearedTypeTaxa} type_taxa_id links, deleted ${deletedVernacular} vernacular names, deleted ${deletedActs} taxonomic acts, and deleted ${deleteTreeDialog.ids.length} records`
          : `Deleted ${deleteTreeDialog.ids.length} records`
      );
      setExternalRefreshKey((k) => k + 1);
      handleCloseDeleteTree();
    } catch (e) {
      showStatus({
        severity: "error",
        title: "Delete failed",
        description: e?.message || "Failed to delete subtree.",
      });
    } finally {
      setDeleteTreeDeleting(false);
    }
  }, [
    deleteTreeDialog.ids,
    deleteTreeDialog.originalParentRefs,
    deleteTreeDialog.typeTaxaRefs,
    deleteTreeDialog.vernacularNameRefs,
    deleteTreeDialog.taxonomicActRefs,
    deleteTreeDeleting,
    deleteTreeAckOriginalParent,
    deleteTreeAckTypeTaxa,
    deleteTreeAckTaxonomicActs,
    deleteTreeAckVernacularNames,
    handleCloseDeleteTree,
    isAdmin,
    showStatus,
    toast,
  ]);

  const filterBanner = childFilterStack.length ? (
    <Box
      sx={{
        mb: 1.5,
        p: 1,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "warning.main",
        bgcolor: (theme) => theme.palette.warning.light + "22", // subtle tint
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Currently filtered by
        </Typography>
        {childFilterStack.map((f, idx) => (
          <Button
            key={f.id}
            size="small"
            color="warning"
            variant="contained"
            endIcon={<CloseIcon fontSize="small" />}
            onClick={() => handleFilterButtonClick(idx)}
            sx={{ textTransform: "none" }}
          >
            {f.label}
          </Button>
        ))}
        <Button
          size="small"
          variant="outlined"
          color="warning"
          onClick={handleClearAllFilters}
          sx={{ textTransform: "none" }}
          startIcon={<CloseIcon fontSize="small" />}
        >
          Clear all
        </Button>
      </Stack>
    </Box>
  ) : null;

  const deleteTreeTargetLabel =
    deleteTreeDialog.target?._nameValidDisp ||
    deleteTreeDialog.target?.name_spell_valid ||
    deleteTreeDialog.target?.id ||
    "";
  const deleteTreePreview = deleteTreeDialog.rows.slice(0, 200);
  const deleteTreeOriginalPreview = deleteTreeDialog.originalParentRefs.slice(0, 200);
  const deleteTreeTypeTaxaPreview = deleteTreeDialog.typeTaxaRefs.slice(0, 200);
  const deleteTreeVernacularPreview = deleteTreeDialog.vernacularNameRefs.slice(0, 200);
  const deleteTreeTaxonomicActPreview = deleteTreeDialog.taxonomicActRefs.slice(0, 200);

  return (
    <>
      <ConsolePageFrame
        title="Scientific Name Data"
        plugin={effectivePlugin}
        searchPlaceholder="Search (name / author / fields...)"
        columnsBuilder={({ onEdit }) =>
          scientificNamesPlugin.columns({
            onEdit,
            onEditDocs: setDocsRow,
            onShowChildrenInline: handleShowChildrenInline,
            activeChildFilterParentId: currentParent?.id || null,
            onAddChild: handleAddChild,
            onBulkMoveChildren: setBulkMoveTarget,
            onDeleteTree: handleOpenDeleteTree,
            canDeleteTree: isAdmin,
          })
        }
        inlineBanner={filterBanner}
        externalRefreshKey={externalRefreshKey}
        onDeleteError={async (error, ids) => {
          const msg = (error?.message || "").toLowerCase();
          const isFk = msg.includes("foreign key") && msg.includes("scientific_names");
          if (!isFk || !ids?.length) return false;

          try {
            const cols = "id,name_spell_valid,current_rank,current_parent,original_parent";
            const fetchCurrent = supabase.from("scientific_names").select(cols).in("current_parent", ids);
            const fetchOriginal = supabase.from("scientific_names").select(cols).in("original_parent", ids);
            const [{ data: curData, error: curErr }, { data: origData, error: origErr }] = await Promise.all([
              fetchCurrent,
              fetchOriginal,
            ]);
            if (curErr || origErr) throw curErr || origErr;

            const map = new Map();
            [...(curData || []), ...(origData || [])].forEach((r) => {
              if (!r?.id) return;
              const existing = map.get(r.id) || r;
              existing._refCurrent = existing._refCurrent || ids.includes(existing.current_parent);
              existing._refOriginal = existing._refOriginal || ids.includes(existing.original_parent);
              map.set(r.id, existing);
            });
            const rows = Array.from(map.values());
            setRefDialog({ open: true, rows, targetIds: ids });
            return true;
          } catch (e) {
            console.error("[ScientificNamesConsole] ref fetch failed", e);
            return false;
          }
        }}
        renderAddDialog={({ onClose }) => (
          <DialogScientificNameEditor open mode="add" onClose={onClose} />
        )}
        renderEditDialog={({ row, onClose }) => (
          <DialogScientificNameEditor open mode="edit" scientificName={row} onClose={onClose} />
        )}
        headerExtras={({ openAdd }) => (
          <>
            <Tooltip title="Import from Excel">
              <IconButton color="primary" onClick={() => setExcelImportOpen(true)}>
                <UploadFileIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Automatically add names via GBIF service">
              <IconButton color="primary" onClick={() => setGbifOpen(true)}>
                <PublicIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Prototype: GBIF (v2)">
              <IconButton color="primary" onClick={() => setGbifV2Open(true)}>
                <NewReleasesIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Prototype: GBIF Hierarchical (v3)">
              <IconButton color="secondary" onClick={() => setGbifV3Open(true)}>
                <NewReleasesIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manually add names">
              <IconButton color="primary" onClick={openAdd}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
        addButtonProps={{ sx: { display: "none" } }}
        toastMessages={{ create: "Scientific name created", update: "Scientific name updated" }}
        initialPageSize={100}
        getRowId={(row) => row.id}
      />
      {gbifOpen && (
        <DialogGbifBulkImport
          open
          onClose={(didUpdate) => {
            // 保存後も作業を継続できるよう、didUpdate時は閉じずに残す
            if (didUpdate) {
              setGbifOpen(true);
              return;
            }
            setGbifOpen(false);
          }}
        />
      )}
      {gbifV2Open && (
        <DialogGbifBulkImportV2
          open
          onClose={(didUpdate) => {
            if (didUpdate) {
              setGbifV2Open(true);
              return;
            }
            setGbifV2Open(false);
          }}
        />
      )}
      {gbifV3Open && (
        <DialogGbifHierarchicalImport
          open
          onClose={() => setGbifV3Open(false)}
        />
      )}
      {excelImportOpen && (
        <DialogScientificNameExcelImport
          open
          onClose={(didUpdate) => {
            setExcelImportOpen(false);
            if (didUpdate) setExternalRefreshKey((k) => k + 1);
          }}
        />
      )}
      {docsRow && (
        <DialogScientificNameDocs
          open
          scientificName={docsRow}
          onClose={() => setDocsRow(null)}
        />
      )}

      {addChildParent && (
        <DialogScientificNameEditor
          open
          mode="add"
          scientificName={{ current_parent: addChildParent.id }}
          onClose={(saved) => {
            setAddChildParent(null);
            if (saved) setExternalRefreshKey((k) => k + 1);
          }}
        />
      )}

      {bulkMoveTarget && (
        <DialogScientificNameBulkMoveChildren
          open
          sourceParent={bulkMoveTarget}
          onClose={(updated) => {
            setBulkMoveTarget(null);
            if (updated) setExternalRefreshKey((k) => k + 1);
          }}
        />
      )}

      {deleteTreeDialog.open && (
        <Dialog open onClose={handleCloseDeleteTree} maxWidth="md" fullWidth>
          <DialogTitle>
            {deleteTreeTargetLabel ? `Delete subtree: ${deleteTreeTargetLabel}` : "Delete subtree"}
          </DialogTitle>
          <DialogContent dividers>
            {deleteTreeDialog.loading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Collecting descendants...</Typography>
              </Stack>
            ) : deleteTreeDialog.error ? (
              <Typography color="error">{deleteTreeDialog.error}</Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This will delete {deleteTreeDialog.ids.length} record(s) (selected + descendants by current_parent).
                </Typography>
                <Box sx={{ height: 360, width: "100%" }}>
                  <DataGrid
                    rows={deleteTreePreview}
                    columns={[
                      { field: "id", headerName: "ID", width: 180 },
                      { field: "name_spell_valid", headerName: "Name", flex: 1, minWidth: 220 },
                      { field: "current_rank", headerName: "Rank", width: 120 },
                      { field: "current_parent", headerName: "Parent", width: 200 },
                    ]}
                    getRowId={(r) => r.id}
                    disableRowSelectionOnClick
                    hideFooter
                  />
                </Box>
                {deleteTreeDialog.rows.length > deleteTreePreview.length && (
                  <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                    Showing first {deleteTreePreview.length} records.
                  </Typography>
                )}
                {deleteTreeDialog.originalParentRefs.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: "warning.main" }}>
                      {deleteTreeDialog.originalParentRefs.length} record(s) outside this subtree reference it via original_parent.
                      These links will be cleared before deletion.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={deleteTreeAckOriginalParent}
                          onChange={(e) => setDeleteTreeAckOriginalParent(e.target.checked)}
                        />
                      }
                      label="I understand original_parent links will be cleared."
                    />
                    <Box sx={{ height: 260, width: "100%", mt: 1 }}>
                      <DataGrid
                        rows={deleteTreeOriginalPreview}
                        columns={[
                          { field: "id", headerName: "ID", width: 180 },
                          { field: "name_spell_valid", headerName: "Name", flex: 1, minWidth: 220 },
                          { field: "current_rank", headerName: "Rank", width: 120 },
                          { field: "original_parent", headerName: "Original Parent", width: 200 },
                        ]}
                        getRowId={(r) => r.id}
                        disableRowSelectionOnClick
                        hideFooter
                      />
                    </Box>
                    {deleteTreeDialog.originalParentRefs.length > deleteTreeOriginalPreview.length && (
                      <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                        Showing first {deleteTreeOriginalPreview.length} records.
                      </Typography>
                    )}
                  </Box>
                )}
                {deleteTreeDialog.typeTaxaRefs.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: "warning.main" }}>
                      {deleteTreeDialog.typeTaxaRefs.length} record(s) outside this subtree reference it via type_taxa_id.
                      These links will be cleared before deletion.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={deleteTreeAckTypeTaxa}
                          onChange={(e) => setDeleteTreeAckTypeTaxa(e.target.checked)}
                        />
                      }
                      label="I understand type_taxa_id links will be cleared."
                    />
                    <Box sx={{ height: 260, width: "100%", mt: 1 }}>
                      <DataGrid
                        rows={deleteTreeTypeTaxaPreview}
                        columns={[
                          { field: "id", headerName: "ID", width: 180 },
                          { field: "name_spell_valid", headerName: "Name", flex: 1, minWidth: 220 },
                          { field: "current_rank", headerName: "Rank", width: 120 },
                          { field: "type_taxa_id", headerName: "Type Taxa", width: 200 },
                        ]}
                        getRowId={(r) => r.id}
                        disableRowSelectionOnClick
                        hideFooter
                      />
                    </Box>
                    {deleteTreeDialog.typeTaxaRefs.length > deleteTreeTypeTaxaPreview.length && (
                      <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                        Showing first {deleteTreeTypeTaxaPreview.length} records.
                      </Typography>
                    )}
                  </Box>
                )}
                {deleteTreeDialog.taxonomicActRefs.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: "warning.main" }}>
                      {deleteTreeDialog.taxonomicActRefs.length} taxonomic act(s) reference this subtree.
                      You can cancel and edit them manually, or delete them below.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={deleteTreeAckTaxonomicActs}
                          onChange={(e) => setDeleteTreeAckTaxonomicActs(e.target.checked)}
                        />
                      }
                      label="I understand these taxonomic acts will be deleted."
                    />
                    <Box sx={{ height: 260, width: "100%", mt: 1 }}>
                      <DataGrid
                        rows={deleteTreeTaxonomicActPreview}
                        columns={[
                          { field: "id", headerName: "ID", width: 180 },
                          {
                            field: "_refs",
                            headerName: "Refers via",
                            width: 180,
                            valueGetter: (p) => (p?.row?._refFields || []).join(", "),
                          },
                          { field: "scientific_name_id", headerName: "Target Name", width: 200 },
                          { field: "related_name_id", headerName: "Related Name", width: 200 },
                          { field: "act_type_id", headerName: "Act Type", width: 140 },
                          { field: "publication_id", headerName: "Publication", width: 180 },
                        ]}
                        getRowId={(r) => r.id}
                        disableRowSelectionOnClick
                        hideFooter
                      />
                    </Box>
                    {deleteTreeDialog.taxonomicActRefs.length > deleteTreeTaxonomicActPreview.length && (
                      <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                        Showing first {deleteTreeTaxonomicActPreview.length} records.
                      </Typography>
                    )}
                  </Box>
                )}
                {deleteTreeDialog.vernacularNameRefs.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: "warning.main" }}>
                      {deleteTreeDialog.vernacularNameRefs.length} vernacular name(s) reference this subtree.
                      You can cancel and edit them manually, or delete them below.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={deleteTreeAckVernacularNames}
                          onChange={(e) => setDeleteTreeAckVernacularNames(e.target.checked)}
                        />
                      }
                      label="I understand these vernacular names will be deleted."
                    />
                    <Box sx={{ height: 260, width: "100%", mt: 1 }}>
                      <DataGrid
                        rows={deleteTreeVernacularPreview}
                        columns={[
                          { field: "id", headerName: "ID", width: 180 },
                          { field: "scientific_name_id", headerName: "Scientific Name", width: 200 },
                        ]}
                        getRowId={(r) => r.id}
                        disableRowSelectionOnClick
                        hideFooter
                      />
                    </Box>
                    {deleteTreeDialog.vernacularNameRefs.length > deleteTreeVernacularPreview.length && (
                      <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                        Showing first {deleteTreeVernacularPreview.length} records.
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteTree} disabled={deleteTreeDeleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={
                deleteTreeDialog.loading ||
                deleteTreeDeleting ||
                !deleteTreeDialog.ids.length ||
                (deleteTreeDialog.originalParentRefs.length > 0 && !deleteTreeAckOriginalParent) ||
                (deleteTreeDialog.typeTaxaRefs.length > 0 && !deleteTreeAckTypeTaxa) ||
                (deleteTreeDialog.taxonomicActRefs.length > 0 && !deleteTreeAckTaxonomicActs) ||
                (deleteTreeDialog.vernacularNameRefs.length > 0 && !deleteTreeAckVernacularNames)
              }
              onClick={handleConfirmDeleteTree}
            >
              {deleteTreeDeleting ? "Deleting..." : "Delete subtree"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {refDialog.open && (
        <Dialog
          open
          onClose={() => setRefDialog({ open: false, rows: [], targetIds: [] })}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            Referencing records prevent delete
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The following records reference the selected ID(s) as parent. Edit or clear the parent link, then retry delete.
            </Typography>
            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={refDialog.rows}
                columns={[
                  { field: "id", headerName: "ID", width: 180 },
                  { field: "name_spell_valid", headerName: "Name", flex: 1, minWidth: 220 },
                  { field: "current_rank", headerName: "Rank", width: 120 },
                  {
                    field: "_refs",
                    headerName: "Refers via",
                    width: 140,
                    valueGetter: (p) => {
                      const flags = [];
                      if (p?.row?._refCurrent) flags.push("current_parent");
                      if (p?.row?._refOriginal) flags.push("original_parent");
                      return flags.join(", ");
                    },
                  },
                  {
                    field: "__actions",
                    headerName: "Actions",
                    width: 200,
                    sortable: false,
                    renderCell: (p) => (
                      <Stack direction="row" spacing={0.5}>
                        <Button size="small" onClick={() => setEditRowInRef(p.row)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="warning"
                          onClick={async () => {
                            const payload = {};
                            if (refDialog.targetIds.includes(p.row.current_parent)) payload.current_parent = null;
                            if (refDialog.targetIds.includes(p.row.original_parent)) payload.original_parent = null;
                            if (!Object.keys(payload).length) return;
                            const { error: updErr } = await supabase
                              .from("scientific_names")
                              .update(payload)
                              .eq("id", p.row.id);
                            if (updErr) {
                              alert(updErr.message || "Failed to clear parent");
                              return;
                            }
                            setRefDialog((prev) => ({
                              ...prev,
                              rows: prev.rows.map((r) =>
                                r.id === p.row.id
                                  ? { ...r, ...payload, _refCurrent: false, _refOriginal: false }
                                  : r
                              ),
                            }));
                            setExternalRefreshKey((k) => k + 1);
                          }}
                        >
                          Clear link
                        </Button>
                      </Stack>
                    ),
                  },
                ]}
                getRowId={(r) => r.id}
                disableRowSelectionOnClick
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRefDialog({ open: false, rows: [], targetIds: [] })}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {editRowInRef && (
        <DialogScientificNameEditor
          open
          mode="edit"
          scientificName={editRowInRef}
          onClose={(saved) => {
            setEditRowInRef(null);
            if (saved) {
              setExternalRefreshKey((k) => k + 1);
              // refetch impacted rows
              if (refDialog.targetIds.length) {
                (async () => {
                  const cols = "id,name_spell_valid,current_rank,current_parent,original_parent";
                  const [cur, orig] = await Promise.all([
                    supabase.from("scientific_names").select(cols).in("current_parent", refDialog.targetIds),
                    supabase.from("scientific_names").select(cols).in("original_parent", refDialog.targetIds),
                  ]);
                  if (cur.error || orig.error) return;
                  const map = new Map();
                  [...(cur.data || []), ...(orig.data || [])].forEach((r) => {
                    if (!r?.id) return;
                    const existing = map.get(r.id) || r;
                    existing._refCurrent = existing._refCurrent || refDialog.targetIds.includes(existing.current_parent);
                    existing._refOriginal = existing._refOriginal || refDialog.targetIds.includes(existing.original_parent);
                    map.set(r.id, existing);
                  });
                  setRefDialog((prev) => ({ ...prev, rows: Array.from(map.values()) }));
                })();
              }
            }
          }}
        />
      )}
    </>
  );
}
